import React, { Fragment, useMemo, useState } from 'react'
import { Download } from '@mui/icons-material'
import Popup from '../../../ui/Popup'
import { DownloadItemStatus, useProgressContext } from '../../../../context/progressContext'
import ProgressItemsList from './ProgressItemsList'
import FilterButtons from './FilterButtons'
import { Button } from '../../../ui'
import { useCancelDownload } from '../../../../hooks/api/documentHooks'

interface DownloadProgressPopupProps {
	items: DownloadItemStatus[]
	isOpen: boolean
	onClose: () => void
}

type Status = DownloadItemStatus['status']
type ItemsFilter = 'all' | 'canceled' | 'completed' | 'failed'

const DownloadProgressPopup: React.FC<DownloadProgressPopupProps> = ({
	items,
	isOpen,
	onClose,
}) => {
	const [filter, setFilter] = useState<ItemsFilter>('all')
	const { setDownloadStatuses } = useProgressContext()
	const { mutateAsync: cancelDownload } = useCancelDownload()

	const mappedItems = useMemo(() => {
		const filtered = filter === 'all'
			? items
			: items.filter((item) => item.status === filter)

		const statusOrder: Record<Status, number> = {
			processing: 1,
			pending: 2,
			completed: 3,
			canceled: 3,
			failed: 3,
		}

		return [...filtered].sort(
			(a, b) => statusOrder[a.status] - statusOrder[b.status],
		)
	}, [filter, items])

	const totalCompleted = items.filter(({ status }) =>
		['completed', 'failed', 'canceled'].includes(status),
	).length
	const totalItems = items.length

	const renderActions = (item: DownloadItemStatus) => {
		if (!['pending', 'processing'].includes(item.status) || !item.taskId) {
			return null
		}

		return (
			<Button
				variant="secondary"
				label="Cancel"
				className="h-9 box-border"
				onClick={async () => {
					await cancelDownload({ taskId: item.taskId! })
					setDownloadStatuses((prev) =>
						prev.map((statusItem) =>
							statusItem.id === item.id
								? { ...statusItem, status: 'canceled' }
								: statusItem,
						),
					)
				}}
			/>
		)
	}

	const filterButtons: { label: string; value: ItemsFilter }[] = [
		{ label: 'All', value: 'all' },
		{ label: 'Canceled', value: 'canceled' },
		{ label: 'Completed', value: 'completed' },
		{ label: 'Failed', value: 'failed' },
	]

	const filterCounts: Record<ItemsFilter, number> = {
		all: items.length,
		canceled: items.filter((f) => f.status === 'canceled').length,
		completed: items.filter((f) => f.status === 'completed').length,
		failed: items.filter((f) => f.status === 'failed').length,
	}

	return (
		<>
			<Popup
				isOpen={isOpen}
				onClose={onClose}
				icon={
					<Download className="fill-text-secondary w-5 h-5 dark:fill-text-main-dark" />
				}
				title={
					<Fragment>
						<span className="dark:text-white">Download</span>
						<span className="font-normal text-base text-text-secondary dark:text-text-main-dark">
							{' '}
							{totalCompleted} of {totalItems}
						</span>
					</Fragment>
				}
				relativePosition={true}
				className="dark:text-text-main-dark dark:bg-background-light-dark"
			>
				<FilterButtons
					buttons={filterButtons}
					activeFilter={filter}
					onFilterChange={setFilter}
					counts={filterCounts}
				/>
				{mappedItems && mappedItems.length > 0 ? (
					<ProgressItemsList
						items={mappedItems}
						showProgressBar={true}
						filterTechnicalErrors={true}
						getItemKey={(item, index) => `${item.id}_${index}`}
						getDisplayName={(item) => item.name}
						isFolder={(item) => item.type === 1}
						renderActions={renderActions}
					/>
				) : (
					<div className="flex items-center justify-center min-h-56 max-h-56 px-0.5 mt-3">
						<p className="text-text-secondary text-xs">No items</p>
					</div>
				)}
			</Popup>
		</>
	)
}

export default DownloadProgressPopup
